import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GeographicFilter, useGeographicFilter } from '../components/geografia/GeographicFilter';
import { ZonesLayer } from '../components/geografia/ZonesLayer';
import axios from 'axios';
import { toast } from 'sonner';
import { Map as MapIcon, Route, MapPinOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AssetsMap from '../components/mapa/AssetsMap';
import PointCard from '../components/mapa/PointCard';
import MapFilters from '../components/mapa/MapFilters';
import MapSidebarList from '../components/mapa/MapSidebarList';
import RecorridoEditor from '../components/recorridos/RecorridoEditor';
import { requestCurrentLocation } from '../components/recorridos/PuntoInput';
import { haversineKm } from '../lib/googleMapsLinks';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SEL_KEY = 'xen_mapa_seleccion';
const EMPTY_F = { search: '', mediador: '', estado: '', etapa_visual: '', tipo_activo_operativo: '', provincia: '', municipio: '' };
const newDraft = (mediadorId) => ({
  nombre: '', fecha_prevista: '', mediador_id: mediadorId || '',
  salida: { tipo: 'texto', texto: '' }, regreso: { tipo: 'texto', texto: '' }, regresar_a_salida: true,
});
const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const toSelItem = (a) => ({
  id: a.id, codigo_npl: a.codigo_npl, direccion: a.direccion, municipio: a.municipio, provincia: a.provincia,
  lat: a.lat ?? null, lng: a.lng ?? null, fuente: a.fuente,
});

// Visita guardada → elemento de selección (con las coordenadas vigentes del activo si han cambiado).
const visitaToSel = (v) => ({
  id: v.asset_id, visita_id: v.visita_id, codigo_npl: v.codigo_npl, direccion: v.direccion, municipio: v.municipio,
  lat: v.coords_actuales?.lat ?? null, lng: v.coords_actuales?.lng ?? null, fuente: v.coords_actuales?.fuente,
});

// Altura disponible hasta el borde inferior de la ventana (descuenta cabecera, filtros y el padding de <main>).
const useFillHeight = (ref, min = 320) => {
  const [h, setH] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const calc = () => {
      const main = el.closest('main');
      const padBottom = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setH(Math.max(min, Math.floor(window.innerHeight - top - padBottom)));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(document.body);
    window.addEventListener('resize', calc);
    return () => { ro.disconnect(); window.removeEventListener('resize', calc); };
  }, [ref, min]);
  return h;
};

export default function MapaPage() {
  const { user } = useAuth();
  const [geoFilter, setGeoFilter] = useGeographicFilter(user?.id);
  const [zones, setZones] = useState([]);
  const [showZones, setShowZones] = useState(true);
  const isAdmin = ['admin', 'superadmin'].includes(user?.role);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isMediador = user?.role === 'mediador';
  const [f, setF] = useState(EMPTY_F);
  const [debounced, setDebounced] = useState(EMPTY_F);
  const [data, setData] = useState({ items: [], sin_coordenadas: [] });
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({});
  const [mediadores, setMediadores] = useState([]);
  const [distancia, setDistancia] = useState({ origen: null, km: 50 });
  const [selection, setSelectionState] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SEL_KEY) || '[]'); } catch { return []; }
  });
  const [activeGroup, setActiveGroup] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [pick, setPick] = useState(null);
  const [editor, setEditor] = useState(null); // { draft, editingId, draftId }
  const [saving, setSaving] = useState(false);
  const [serverProblems, setServerProblems] = useState([]);
  const [tab, setTab] = useState('resultados');
  const [dataVersion, setDataVersion] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [prevSelection, setPrevSelection] = useState(null);
  const mapAreaRef = useRef(null);
  const fillH = useFillHeight(mapAreaRef);
  const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;

  const setSelection = useCallback((next) => {
    setSelectionState(prev => {
      const val = typeof next === 'function' ? next(prev) : next;
      sessionStorage.setItem(SEL_KEY, JSON.stringify(val));
      return val;
    });
  }, []);

  useEffect(() => { const t = setTimeout(() => setDebounced(f), 350); return () => clearTimeout(t); }, [f]);

  useEffect(() => {
    axios.get(`${API}/filters`, { withCredentials: true }).then(r => setOptions(r.data)).catch(() => {});
    axios.get(`${API}/exclusion-geografica/zonas`, { withCredentials: true }).then(r => setZones(r.data)).catch(() => toast.error('No se pudieron cargar las zonas de exclusión'));
    axios.get(`${API}/recorridos/mediadores`, { withCredentials: true }).then(r => setMediadores(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    let active = true;
    const qs = new URLSearchParams(Object.entries(debounced).filter(([, v]) => v));
    qs.set('exclusion_geografica', geoFilter);
    axios.get(`${API}/mapa/activos?${qs.toString()}`, { withCredentials: true })
      .then(r => { if (active) { setData(r.data); setActiveGroup(null); setDataVersion(v => v + 1); } })
      .catch(err => toast.error(err.response?.data?.detail || 'No se pudo cargar el mapa'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [debounced, geoFilter]);

  // Abrir un recorrido guardado para editar o duplicar.
  useEffect(() => {
    const rid = params.get('recorrido') || params.get('duplicar');
    if (!rid) return;
    const duplicar = !!params.get('duplicar');
    axios.get(`${API}/recorridos/${rid}`, { withCredentials: true }).then(r => {
      const rec = r.data;
      const disponibles = rec.visitas.filter(v => v.disponible);
      const omitidas = rec.visitas.length - disponibles.length;
      setPrevSelection(JSON.parse(sessionStorage.getItem(SEL_KEY) || '[]'));
      if (omitidas) toast.warning(`${omitidas} visita(s) ya no están disponibles y se han retirado de la selección. Revisa el recorrido antes de guardar.`, { duration: 8000 });
      setSelection(disponibles.map(v => (duplicar ? { ...visitaToSel(v), visita_id: undefined } : visitaToSel(v))));
      setEditor({
        editingId: duplicar ? null : rec.id,
        draftId: uuid(),
        draft: {
          nombre: duplicar ? `Copia de ${rec.nombre}` : rec.nombre,
          fecha_prevista: rec.fecha_prevista,
          mediador_id: isMediador ? user.id : rec.mediador_id,
          salida: rec.salida, regreso: rec.regreso || { tipo: 'texto', texto: '' },
          regresar_a_salida: !!rec.regresar_a_salida,
        },
      });
      setParams({}, { replace: true });
    }).catch(err => toast.error(err.response?.data?.detail || 'No se pudo abrir el recorrido'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const visibles = useMemo(() => {
    if (!distancia.origen) return data.items;
    return data.items.filter(a => haversineKm(distancia.origen, a) <= distancia.km);
  }, [data.items, distancia]);

  const groups = useMemo(() => {
    const m = new Map();
    visibles.forEach(a => {
      const k = `${a.lat.toFixed(6)},${a.lng.toFixed(6)}`;
      if (!m.has(k)) m.set(k, { key: k, lat: a.lat, lng: a.lng, items: [] });
      m.get(k).items.push(a);
    });
    return [...m.values()];
  }, [visibles]);

  const selectedIds = useMemo(() => new Set(selection.map(s => s.id)), [selection]);
  const selectedOrder = useMemo(() => new Map(selection.map((s, i) => [s.id, i + 1])), [selection]);
  // Vista inicial: España. Tras cambiar filtros o distancia, se ajusta a los resultados visibles.
  const fitKey = useMemo(() => (dataVersion <= 1 && !distancia.origen ? 'spain' : `${dataVersion}|${JSON.stringify(distancia)}`), [dataVersion, distancia]);

  const toggle = useCallback((a) => {
    setSelection(prev => (prev.some(s => s.id === a.id) ? prev.filter(s => s.id !== a.id) : [...prev, toSelItem(a)]));
  }, [setSelection]);

  const focusAsset = (a) => {
    const g = groups.find(x => x.items.some(i => i.id === a.id));
    if (g) { setActiveGroup(g); setFlyTarget({ lat: g.lat, lng: g.lng, t: Date.now() }); }
  };

  const onMapClick = (p) => {
    if (!pick) return;
    if (pick === 'distancia') setDistancia(d => ({ ...d, origen: p }));
    else setEditor(e => ({ ...e, draft: { ...e.draft, [pick]: { tipo: 'punto', lat: p.lat, lng: p.lng, texto: null } } }));
    setPick(null);
  };

  const useLocationForDistance = async () => {
    try { const p = await requestCurrentLocation(); setDistancia(d => ({ ...d, origen: p })); } catch (e) { toast.error(e.message); }
  };

  const openEditor = () => {
    setServerProblems([]);
    setEditor({ editingId: null, draftId: uuid(), draft: newDraft(isMediador ? user.id : '') });
  };

  const save = async () => {
    const { draft, editingId, draftId } = editor;
    const body = {
      ...draft, id: editingId ? undefined : draftId,
      regreso: draft.regresar_a_salida ? null : draft.regreso,
      visitas: selection.map(a => ({ asset_id: a.id, visita_id: a.visita_id })),
    };
    setSaving(true); setServerProblems([]);
    try {
      const r = editingId
        ? await axios.put(`${API}/recorridos/${editingId}`, body, { withCredentials: true })
        : await axios.post(`${API}/recorridos`, body, { withCredentials: true });
      toast.success('Recorrido guardado y visitas registradas en los activos');
      setSelection(prevSelection || []); setPrevSelection(null); setEditor(null);
      navigate(`/recorridos/${r.data.id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object') { setServerProblems(d.problemas || []); toast.error(d.message); } else toast.error(d || 'No se pudo guardar el recorrido. Tu selección se conserva; reinténtalo.');
    } finally { setSaving(false); }
  };

  const pickPoints = editor ? [
    editor.draft.salida?.tipo === 'punto' && { key: 's', lat: editor.draft.salida.lat, lng: editor.draft.salida.lng, label: 'S', tone: 'start' },
    !editor.draft.regresar_a_salida && editor.draft.regreso?.tipo === 'punto' && { key: 'r', lat: editor.draft.regreso.lat, lng: editor.draft.regreso.lng, label: 'R', tone: 'end' },
  ].filter(Boolean) : [];
  if (distancia.origen) pickPoints.push({ key: 'd', lat: distancia.origen.lat, lng: distancia.origen.lng, label: '◎', tone: 'origin' });

  return (
    <div className="space-y-3" data-testid="mapa-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><MapIcon className="w-5 h-5" /> Mapa de activos</h1>
          <p className="text-sm text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span data-testid="map-count-visible">{loading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : `${visibles.length} en el mapa`}</span>
            <button onClick={() => setTab('sin')} className="flex items-center gap-1 text-amber-700 hover:underline" data-testid="map-count-sin-coords">
              <MapPinOff className="w-3.5 h-3.5" /> {data.sin_coordenadas.length} pendientes de localizar
            </button>
            {distancia.origen && <span className="text-blue-700" data-testid="map-distance-label">Radio {distancia.km} km en línea recta</span>}
          </p>
        </div>
        {!editor && (
          <button onClick={openEditor} className="btn-primary text-sm flex items-center gap-2" data-testid="crear-recorrido-btn">
            <Route className="w-4 h-4" /> Crear recorrido {selection.length > 0 && `(${selection.length})`}
          </button>
        )}
      </div>
      <button onClick={() => setShowFilters(v => !v)} className="btn-secondary text-sm w-full sm:hidden" data-testid="map-toggle-filters">
        {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
      </button>
      <div className="flex flex-wrap items-end gap-3" data-testid="map-geographic-toolbar">
        <div className="w-full sm:w-56"><GeographicFilter value={geoFilter} onChange={setGeoFilter} testId="map-filter-exclusion-geografica" /></div>
        <label className="text-xs flex items-center gap-2 py-2"><input type="checkbox" checked={showZones} onChange={e => setShowZones(e.target.checked)} data-testid="map-zones-toggle" />Mostrar zonas</label>
        {isAdmin && <Link to="/zonas-exclusion" className="btn-secondary text-xs" data-testid="map-manage-zones">Gestionar zonas de exclusión</Link>}
      </div>
      <div className={`card-base p-3 ${showFilters ? '' : 'hidden'} sm:block`}>
        <MapFilters f={f} setF={setF} options={options} mediadores={mediadores} showMediador={!isMediador}
          distancia={distancia} setDistancia={setDistancia} onUseLocation={useLocationForDistance}
          onPickOnMap={() => setPick(pick === 'distancia' ? null : 'distancia')} pickingDistance={pick === 'distancia'} />
      </div>
      <div ref={mapAreaRef} className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]" style={isDesktop && fillH ? { height: fillH } : undefined}>
        <div className="relative min-h-0" style={{ height: isDesktop ? '100%' : (fillH || 420) }} data-testid="map-area">
          <AssetsMap groups={groups} selectedOrder={selectedOrder} onGroupClick={setActiveGroup} onMapClick={onMapClick}
            fitKey={fitKey} flyTarget={flyTarget} radius={distancia.origen ? { ...distancia.origen, km: distancia.km } : null}
            extraPoints={pickPoints} picking={!!pick}>
            {showZones && <ZonesLayer zones={zones} />}
          </AssetsMap>
          {pick && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] bg-blue-600 text-white text-xs px-3 py-1.5 rounded-full shadow" data-testid="map-pick-hint">
              Pulsa en el mapa para fijar {pick === 'distancia' ? 'el origen de distancia' : pick === 'salida' ? 'la salida' : 'el regreso'}
            </div>
          )}
          <PointCard group={activeGroup} selectedIds={selectedIds} onToggle={toggle} onClose={() => setActiveGroup(null)} />
        </div>
        <div className="card-base p-4 min-h-0 lg:overflow-y-auto" data-testid="map-side-panel">
          {editor ? (
            <RecorridoEditor draft={editor.draft} setDraft={(fn) => setEditor(e => ({ ...e, draft: typeof fn === 'function' ? fn(e.draft) : fn }))}
              selection={selection} setSelection={setSelection} mediadores={mediadores} isMediador={isMediador}
              pick={pick} setPick={setPick} onSave={save} saving={saving} editingId={editor.editingId}
              serverProblems={serverProblems} onCancel={() => {
                setEditor(null); setPick(null);
                // Al cancelar la edición de un recorrido guardado se recupera la selección previa del usuario.
                if (prevSelection) { setSelection(prevSelection); setPrevSelection(null); }
              }} />
          ) : (
            <MapSidebarList tab={tab} setTab={setTab} items={visibles} sinCoords={data.sin_coordenadas}
              selection={selection} selectedIds={selectedIds} onToggle={toggle} onFocus={focusAsset}
              onClearSelection={() => setSelection([])} />
          )}
        </div>
      </div>
    </div>
  );
}
